import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserProfile } from "@/types/profile";
import { User, Mail, Shield, ChevronRight, Phone, MapPin, Building2 } from "lucide-react";

interface UserProfileCardProps {
    profile: UserProfile | null;
    userName: string | null;
    userEmail: string | null;
    profileImage: string;
    currentSession?: { location?: string; ip?: string } | null;
}

export function UserProfileCard({ profile, userName, userEmail, profileImage, currentSession }: UserProfileCardProps) {
    const navigate = useNavigate();

    const userInitials = userName
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "U";

    return (
        <Card
            onClick={() => navigate("/me")}
            className="group cursor-pointer border-border/50 hover:border-primary/50 hover:shadow-lg transition-all duration-300 bg-background/50 backdrop-blur-sm px-3 py-3  space-y-4 mb-6"
        >


            <div className="relative flex items-end gap-3.5">
                {/* Avatar Group */}
                <div className="shrink-0">
                    <Avatar className="w-20 h-20 border-[3px] border-background shadow-md relative">
                        <AvatarImage src={profileImage} className="object-cover" />
                        <AvatarFallback className="bg-primary/10 text-primary font-bold text-xl">
                            {userInitials}
                        </AvatarFallback>
                    </Avatar>
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0 pb-1.5">
                    <h3 className="font-bold text-lg leading-tight group-hover:text-primary transition-colors truncate">
                        {profile?.name || userName || "User"}
                    </h3>
                    {/* Role Badge (Absolute Top Right) */}
                    {profile?.role && (
                        <Badge
                            variant="secondary"
                            className="bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        >
                            {profile.role}
                        </Badge>
                    )}
                </div>


            </div>
            {/* Details List */}
            <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3 text-muted-foreground group-hover:text-foreground/80 transition-colors">
                    <Mail className="w-4 h-4 shrink-0 mt-0.5 text-primary/70" />
                    <span className="break-all line-clamp-1">{userEmail}</span>
                </div>

                {profile?.phone && (
                    <div className="flex items-center gap-3 text-muted-foreground group-hover:text-foreground/80 transition-colors">
                        <Phone className="w-4 h-4 shrink-0 text-primary/70" />
                        <span>{profile.phone}</span>
                    </div>
                )}

                {currentSession?.location && (
                    <div className="flex items-center gap-3 text-muted-foreground group-hover:text-foreground/80 transition-colors">
                        <MapPin className="w-4 h-4 shrink-0 text-primary/70" />
                        <span>{currentSession.location}</span>
                    </div>
                )}

                {(profile?.business || profile?.team) && (
                    <div className="flex items-start gap-3 text-muted-foreground group-hover:text-foreground/80 transition-colors">
                        <Building2 className="w-4 h-4 shrink-0 mt-0.5 text-primary/70" />
                        <span className="leading-snug">
                            {[profile.business, profile.team].filter(Boolean).join(" • ")}
                        </span>
                    </div>
                )}
            </div>
        </Card>
    );
}
